import { useCallback } from 'react'
import { ApolloError, useMutation } from '@apollo/client'
import { AdminAPI, GraphQL } from '@guestgoodie/graphql'

import * as Form from '@/features/form'

export type FormData = {
  state: GraphQL.StayStates
  scheduledStart: Date
  scheduledStop: Date
  guestIds: number[]
  spaceId?: number | null
}

interface Options {
  id?: number
  onDone?: () => void
}

export default function useStayForm(options: Options) {
  const { id, onDone } = options

  const [createStay] = useMutation(AdminAPI.stay.CREATE, {
    refetchQueries: [AdminAPI.stay.QUERY, AdminAPI.stay.GET]
  })

  const [updateStay] = useMutation(AdminAPI.stay.UPDATE, {
    refetchQueries: [
      {
        query: AdminAPI.stay.GET,
        variables: {
          id,
          withSpace: true,
          withGuests: true
        }
      }
    ]
  })

  const { parseApolloErrors, errors } = Form.useErrorHelpers<FormData>({})

  const create = useCallback(
    async (data: FormData) => {
      try {
        const response = await createStay({
          variables: {
            input: data
          }
        })

        if (response.data) onDone?.()
        if (response.errors) parseApolloErrors(response.errors)
      } catch (error) {
        if (error instanceof ApolloError) {
          return parseApolloErrors(error.graphQLErrors)
        }

        // re-throw so we catch it somewhere else
        throw error
      }
    },
    [onDone, createStay, parseApolloErrors]
  )

  const update = useCallback(
    async (data: FormData) => {
      if (!id) return

      try {
        const response = await updateStay({
          variables: {
            input: {
              ...data,
              id
            }
          }
        })

        if (response.data) onDone?.()
        if (response.errors) parseApolloErrors(response.errors)
      } catch (error) {
        if (error instanceof ApolloError) {
          return parseApolloErrors(error.graphQLErrors)
        }

        // re-throw so we catch it somewhere else
        throw error
      }
    },
    [id, onDone, updateStay, parseApolloErrors]
  )

  return {
    errors,
    create,
    update
  }
}
